import { NonIdealState } from "@blueprintjs/core";
import { FormikProps } from "formik";
import {
  ButtonsBloc,
  FGCustomPanel,
  FGListen,
  FGSelectInput,
  FGTextAreaInput,
  FieldGroup,
  FieldSet,
  InlineButtonContainer,
} from "nsitools-react";
import * as React from "react";
import { useHistory } from "react-router";
import * as Yup from "yup";

import {
  AffixRequestApi,
  EPlatform,
  ERequestState,
  KennelOwnerApi,
  KennelOwnerSearch,
  KennelOwnershipChangeRequestDto,
  PersonApi,
  RequestApi,
  RequestDto,
  RequestInfoDto,
} from "../../../../../api";
import {
  CancelButton,
  FGPeopleSearchInput,
  FGSRSHDateMaskInput,
  SharepointButton,
  SmallFormGenerator,
} from "../../../../../components";
import { useAuth, useDialog, useEventsContext } from "../../../../../contexts";
import { ERoutes } from "../../../../../ERoutes";
import { useApiService, useCrudApi, useTl, useWindow } from "../../../../../hooks";
import { useReferential } from "../../../../../hooks/useReferential";
import { ETLCodes } from "../../../../../locales";
import { RequestTransientPersonSelection } from "../../../request/item/RequestTransientPersonSelection";

export interface IKennelOwnershipChangeRequestDetailProps {
  kennelOwnershipChangeRequestId: number;
  editMode: boolean;
  requestInfo: RequestInfoDto;
}

export const KennelOwnershipChangeRequestDetail: React.FunctionComponent<IKennelOwnershipChangeRequestDetailProps> = ({
  kennelOwnershipChangeRequestId,
  editMode,
}) => {
  const { t } = useTl();
  const history = useHistory();
  const api = useApiService(AffixRequestApi);
  const personApi = useApiService(PersonApi);
  const kennelOwnerApi = useApiService(KennelOwnerApi);
  const { user } = useAuth();
  const { openNewTab } = useWindow();
  const { showDialogPromise } = useDialog();

  const [openedKennels, okloading] = useReferential((a) => a.getOpenedKennels(data?.kennelId), true, []);
  const [selectorError, setselectorError] = React.useState("");

  const [ownerIdsBeforeEdition, setOwnerIdsBeforeEdition] = React.useState([]);
  const [kennelId, setKennelId] = React.useState<number>();

  // =========================== DATA MANIPULATION ==================================================

  const { data, loading, saving, saveItem, refresh } = useCrudApi<KennelOwnershipChangeRequestDto>(
    React.useMemo(
      () => ({
        getApiFn: (id = kennelOwnershipChangeRequestId) =>
          id > 0
            ? api.getKennelOwnershipChangeRequest(id)
            : new KennelOwnershipChangeRequestDto({
                transientOwnerIds: [],
                request: new RequestDto({
                  status: ERequestState.REQUESTED,
                  remark: "",
                  requestDate: new Date(),
                }),
              }).toJSON(),
        saveApiFn: (d: KennelOwnershipChangeRequestDto) => {
          const id = api.saveKennelOwnershipChangeRequest(d);
          const updatedOwnerIds = [...ownerIdsBeforeEdition];
          d.transientOwnerIds?.forEach((ownerId) => {
            if (!updatedOwnerIds.includes(ownerId)) {
              updatedOwnerIds.push(ownerId);
            }
          });
          setOwnerIdsBeforeEdition(updatedOwnerIds);
          return id;
        },
        onSavedRoute: (d) => `${ERoutes.affixRequest}/${d.kennelOwnershipChangeRequestId}/detail/edit/kennelOwnership`,
      }),
      [api, kennelOwnershipChangeRequestId, ownerIdsBeforeEdition]
    ),
    [kennelOwnershipChangeRequestId]
  );

  const onKennelIdChange = React.useCallback(
    async (value: number, formik: FormikProps<KennelOwnershipChangeRequestDto>) => {
      setKennelId(value);
      if (value !== data?.kennelId) {
        const fromDb = await kennelOwnerApi.getIds(
          KennelOwnerSearch.fromJS({
            kennelId: value,
          })
        );
        (window as any)._MYFORMIK = formik;
        setOwnerIdsBeforeEdition(fromDb);
        formik.setFieldValue("transientOwnerIds", fromDb);
      } else {
        formik.setFieldValue("transientOwnerIds", data?.transientOwnerIds);
      }
    },
    [data, kennelOwnerApi]
  );

  const computeError = React.useCallback(
    (ids) => {
      if (ids === data?.transientOwnerIds || user.platform === EPlatform.PORTAL) {
        return "";
      }
      if (kennelOwnershipChangeRequestId <= 0 || data?.request?.status === ERequestState.REQUESTED) {
        ids.forEach(async (element) => {
          const result = await personApi.isPersonBelgian(element);
          if (!result.value) {
            setselectorError(t(ETLCodes.OneOrMoreOwnersNotBelgian));
            return;
          }
        });
        ids.forEach(async (element) => {
          console.log("checking for ", element);
          console.log("kennelId ", kennelId);
          const result = await personApi.isKennelOwner(element, kennelId, new Date(), null);
          if (result.value) {
            setselectorError(t(ETLCodes.OneOrMorPersonAlreadyOwnsAKennel));
            return;
          }
        });
        setselectorError("");
        return;
      }
    },
    [data?.request?.status, data?.transientOwnerIds, kennelId, kennelOwnershipChangeRequestId, personApi, t]
  );

  const [saveChecking, setSaveChecking] = React.useState(false);
  const saveCheck = React.useCallback(
    async (values) => {
      setSaveChecking(true);
      if (selectorError !== "") {
        return;
      }
      let isBanned = false;
      if (kennelOwnershipChangeRequestId <= 0) {
        isBanned = (await personApi.isPersonExcludedQuery(values?.request?.personId)).value;
      }
      if (isBanned) {
        const isbannedValidation = await showDialogPromise({
          message: t(ETLCodes.PersonIsBannedConfirmMessage),
          title: t(ETLCodes.Warning),
        });
        if (isbannedValidation !== "yes") return;
      }
      setSaveChecking(false);
      await saveItem(values);
    },
    [kennelOwnershipChangeRequestId, personApi, saveItem, selectorError, showDialogPromise, t]
  );

  // =========================== EVENT MANAGER ==================================================
  const onRequestRefreshEvent = React.useCallback(() => {
    refresh();
  }, [refresh]);

  const { subscribeToEvent, unsubscribeEvent } = useEventsContext();
  React.useEffect(() => {
    subscribeToEvent("RequestRefresh", onRequestRefreshEvent);
    return () => unsubscribeEvent("RequestRefresh", onRequestRefreshEvent);
  }, [onRequestRefreshEvent, subscribeToEvent, unsubscribeEvent]);

  // =========================== FORM ==================================================
  const RequestedFormSchema = React.useMemo(() => {
    return Yup.object().shape({
      transientOwnerIds: Yup.array().of(
        Yup.number().test("is-begian", t(ETLCodes.AtLeastOneOwnerIsNotBelgian), async function (value) {
          if (kennelOwnershipChangeRequestId <= 0 || data?.request?.status === ERequestState.REQUESTED) {
            const result = await personApi.isPersonBelgian(value);
            return result.value;
          }
          return true;
        })
      ),
      kennelId: Yup.number().nullable(),
      //transientOwnerIds: Yup.array().test("is-begian", t(ETLCodes.AtLeastOneOwnerIsNotBelgian), computeError),
      request: Yup.object().shape({
        personId: Yup.number()
          .nullable()
          .required(t(ETLCodes.Required))
          .test("missing-adress-information", t(ETLCodes.MissingCompleteAdressOrEmail), async function (value) {
            if (kennelOwnershipChangeRequestId <= 0 || data?.request?.status === ERequestState.REQUESTED) {
              if (value !== 0 && value !== null && value !== undefined) {
                const result = await personApi.missingAddressInformationCheck(value);
                return result.value;
              }
              return true;
            }
            return true;
          })
          .test("is-begian", t(ETLCodes.IsNotBelgian), async function (value) {
            if (kennelOwnershipChangeRequestId <= 0 || data?.request?.status === ERequestState.REQUESTED) {
              const result = await personApi.isPersonBelgian(value);
              return result.value;
            }
            return true;
          }),
        requestDate: Yup.date().nullable().required(t(ETLCodes.Required)),
      }),
    });
  }, [data, kennelOwnershipChangeRequestId, personApi, t]);

  const OtherFormSchema = React.useMemo(() => {
    return Yup.object().shape({});
  }, []);

  const statusEditable = React.useMemo(() => {
    if (data) {
      return (
        data?.request?.status === ERequestState.REQUESTED ||
        data?.request?.status === ERequestState.AWAITING_APPLICANT_REACTION ||
        data?.request?.status === ERequestState.BILLED ||
        data?.request?.status === ERequestState.PAID
      );
    }
    return false;
  }, [data]);

  const requestApi = useApiService(RequestApi);
  const [shpLoading, setShpLoading] = React.useState(false);
  const existsInShp = React.useMemo(() => !!data?.request.sharepointUri, [data?.request]);
  const openOrCreateInShp = React.useCallback(async () => {
    setShpLoading(true);
    if (!!data?.request.sharepointUri) {
      window.open(data?.request.sharepointUri, "_blank");
      setShpLoading(false);
    } else {
      try {
        await requestApi.createInSharepoint(data?.requestId);
        refresh();
      } catch (e) {
        throw e;
      } finally {
        setShpLoading(false);
      }
    }
  }, [data?.request.sharepointUri, data?.requestId, requestApi, refresh]);

  return (
    <>
      <SmallFormGenerator
        initialValues={data}
        editMode={editMode}
        onSubmit={saveCheck}
        validationSchema={data?.request?.status === ERequestState.REQUESTED ? RequestedFormSchema : OtherFormSchema}
        loading={loading}
        onCancel={() => history.push(ERoutes.affixRequest)}
        saving={saving || saveChecking}
        hideButtons={data?.request?.status === ERequestState.EXECUTED}
      >
        <FGListen field="kennelId" onChanged={onKennelIdChange} />
        <FGListen field="transientOwnerIds" onChanged={computeError} />
        <FieldSet
          title={t(ETLCodes.KennelOwnershipChange)}
          rightElement={
            kennelOwnershipChangeRequestId > 0 && (
              <SharepointButton existsInShp={existsInShp} loading={shpLoading} onClick={openOrCreateInShp} />
            )
          }
        >
          <FieldGroup columns={2}>
            <div>
              <FGSRSHDateMaskInput
                name="request.requestDate"
                label={t(ETLCodes.RequestDate)}
                requiredMark
                readonly={!statusEditable || data?.request?.isBlocked}
              />
              <FGSelectInput
                name="kennelId"
                label={t(ETLCodes.Kennel)}
                items={openedKennels}
                loading={okloading}
                onItemViewClick={(item) => openNewTab(`${ERoutes.kennel}/${item}/detail/view`)}
                readonly={!statusEditable || data?.request?.isBlocked}
                requiredMark
              />
              <FGPeopleSearchInput
                name="request.personId"
                label={t(ETLCodes.Contact)}
                onItemViewClick={(item) => openNewTab(`${ERoutes.people}/${item}/detail/view`)}
                readonly={!statusEditable || data?.request?.isBlocked}
                requiredMark
                showWarning={true}
              />
            </div>
            <FGTextAreaInput label={t(ETLCodes.Remark)} name="request.remark" growVertically rows={6} />
          </FieldGroup>
        </FieldSet>
        {user.platform === EPlatform.PORTAL ? null : (
          <FieldSet title={t(ETLCodes.Owners)}>
            <FGCustomPanel>
              {({ formik }) =>
                formik?.values?.kennelId ? (
                  <>
                    <RequestTransientPersonSelection
                      linkedIds={formik?.values?.transientOwnerIds || null}
                      onLinkedIdsChanged={(ids) => formik?.setFieldValue("transientOwnerIds", ids)}
                      editMode={editMode && !data?.request?.isBlocked && statusEditable}
                      loading={loading}
                      kennelId={formik?.values?.kennelId}
                      errors={selectorError}
                    />
                  </>
                ) : (
                  <NonIdealState icon="resolve" description={t(ETLCodes.PleaseSelectAKennelToEditOwners)} />
                )
              }
            </FGCustomPanel>
          </FieldSet>
        )}
        {data?.request?.status === ERequestState.EXECUTED && (
          <InlineButtonContainer>
            <div />
            <ButtonsBloc style={{ float: "right" }}>
              <CancelButton minimal={false} onClick={() => history.push(`${ERoutes.affixRequest}`)} />
            </ButtonsBloc>
          </InlineButtonContainer>
        )}
      </SmallFormGenerator>
    </>
  );
};
